import type Excel from 'exceljs';
import { saveAs } from 'file-saver';
import { range, difference, isEmpty } from 'lodash/fp';

export type ExcelFile = ArrayBuffer | string;

export const headCellStyle: Excel.FillPattern = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'd9d8d9' },
};

export const applyHeaderStyle = (worksheet: Excel.Worksheet, headerRowNumber: number = 1) => {
    const headerRow = worksheet.getRow(headerRowNumber);

    for (let i = 1; i <= headerRow.cellCount; i++) {
        const cell = headerRow.getCell(i);
        cell.fill = headCellStyle;
        if (cell.value.toString().includes('*')) {
            cell.font = { name: 'Arial' };
            cell.value = {
                richText: [
                    { text: cell.value.toString().slice(0, -1) },
                    { font: { color: { argb: 'FFFF0000' } }, text: '*' },
                ],
            };
        }
        const column = worksheet.getColumn(i);
        column.style = { font: { name: 'Arial' } };
        column.width = 20;
    }
};

export const applyCellStyle = (worksheet: Excel.Worksheet) => {
    for (let i = 1; i <= worksheet.columnCount; i++) {
        const column = worksheet.getColumn(i);
        column.style = { font: { name: 'Arial' } };
        column.eachCell((cell, colNumber) => {
            const cellData = cell;
            cellData.font = { name: 'Arial' };
        });
    }
};

const getColumns = (mappedHeaders: Record<string, string>) =>
    Object.keys(mappedHeaders).reduce(
        (array, keyValue) => [...array, { header: keyValue, key: mappedHeaders[keyValue] }],
        []
    );

export const getWorkbookToExport = async (mappedHeaders: Record<string, string>) => {
    const Excel = await import('exceljs').then(imported => imported.default);

    const workbook = new Excel.Workbook();
    const worksheet = workbook.addWorksheet();
    const columns = getColumns(mappedHeaders);

    worksheet.columns = columns;

    return workbook;
};

export const exportFinancialTabsExcel = (
    workbook: Excel.Workbook,
    productName: string,
    tableName: string,
    headerRowNumber: number = 1
) => {
    const worksheet = workbook.getWorksheet(1);

    applyHeaderStyle(worksheet, headerRowNumber);
    applyCellStyle(worksheet);

    workbook.xlsx.writeBuffer().then(data => {
        const blob = new Blob([data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        saveAs(blob, `${productName} - ${tableName}.xlsx`);
    });
};

const getHeaderIndexes = (sheet: Excel.Worksheet, headers: Record<string, string>, headerRowNumber: number = 1) => {
    const row = sheet.getRow(headerRowNumber);
    const headerIndexes = [];

    row.eachCell({ includeEmpty: true }, (cell, col) => {
        headerIndexes[col] = headers[cell.text];
    });

    return headerIndexes;
};

const getHeaderByKey = (headerKey: string, headers: Record<string, string>) => {
    if (!headerKey) {
        return null;
    }

    return Object.entries(headers).find(([key, value]) => value === headerKey);
};

const parseRowData = (
    sheet: Excel.Worksheet,
    row: Excel.Row,
    headers: Record<string, string>,
    headerRowNumber: number = 1,
    skipData: boolean = false
) => {
    const data = {};
    const missingRequiredFields: string[] = [];

    const headerIndexes = getHeaderIndexes(sheet, headers, headerRowNumber);

    row.eachCell({ includeEmpty: true }, (cell, col) => {
        // skip if there is no header for the colum
        const hasHeader = !isEmpty(headerIndexes[col]?.trim());
        if (hasHeader) {
            const [headerTitle] = getHeaderByKey(headerIndexes[col], headers);

            // check if required field is empty
            const isRequired = headerTitle?.includes('*');
            if (isRequired && isEmpty(cell.text?.trim())) {
                missingRequiredFields.push(headerTitle);
            }

            // if there is error, data will be no use
            if (!skipData || (missingRequiredFields.length === 0 && !isEmpty(cell.text?.trim()))) {
                data[headerIndexes[col]] =
                    !Number.isNaN(parseFloat(cell.text)) && !Number.isNaN(Number(cell.text)) ? +cell.text : cell.text;
            }
        }
    });

    return {
        data,
        missingRequiredFields,
    };
};

const getData = (sheet: Excel.Worksheet, headers: Record<string, string>, headerRowNumber: number = 1) => {
    const errors: string[] = [];
    const list = [];

    // skip empty row
    sheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
        // skip header row
        if (rowNumber > headerRowNumber) {
            // parse data or find any empty required fields
            const { data, missingRequiredFields } = parseRowData(
                sheet,
                row,
                headers,
                headerRowNumber,
                errors.length > 0
            );

            if (missingRequiredFields?.length > 0) {
                errors.push(`Empty Required Field/s at row #${rowNumber}: ${missingRequiredFields.join(', ')}`);
            }

            if (!isEmpty(data)) {
                list.push(data);
            }
        }
    });

    return { errors, data: list };
};

const getMissingHeaders = (
    sheet: Excel.Worksheet,
    mappedHeaders: Record<string, string>,
    headerRowNumber: number = 1
) => {
    const row = sheet.getRow(headerRowNumber);
    const requiredHeaders = Object.keys(mappedHeaders).filter(header => header.includes('*'));

    return difference(
        requiredHeaders,
        requiredHeaders.filter(header => range(1, sheet.columnCount + 1).some(i => row.getCell(i).text === header))
    );
};

export const getWorkbook = async (excel: ExcelFile): Promise<Excel.Workbook> => {
    const Excel = await import('exceljs').then(imported => imported.default);

    const workbook = new Excel.Workbook();

    if (typeof excel === 'string') {
        return workbook.xlsx.readFile(excel);
    }

    if (excel instanceof ArrayBuffer) {
        return workbook.xlsx.load(excel);
    }

    throw new Error('Excel file data type is not supported!');
};

export type GetDataFromExcelProps = {
    excel: ExcelFile;
    mappedHeaders: Record<string, string>;
    sheetIndexOrName?: string | number;
    headerRowNumber?: number;
};

export const getDataFromExcel = async ({
    excel,
    mappedHeaders,
    sheetIndexOrName = 1,
    headerRowNumber = 1,
}: GetDataFromExcelProps) => {
    const workbook = await getWorkbook(excel);
    const worksheet = workbook.getWorksheet(sheetIndexOrName);

    const errors: string[] = [];

    const missingHeaders = getMissingHeaders(worksheet, mappedHeaders);

    if (missingHeaders.length > 0) {
        errors.push(`Missing Header/s: ${missingHeaders.join(', ')}. Please use template and re-upload.`);
    }

    if (errors.length > 0) {
        return { errors, data: null };
    }

    const { errors: err, data } = getData(worksheet, mappedHeaders, headerRowNumber);

    if (!data?.length) {
        errors.push(`Thers's no data to import. Please fill in data and re-upload.`);

        return { errors, data: null };
    }

    return { errors: err, data };
};
